import type { FC } from "react";

import { getPageGridClass } from "./grid";

import { AssessmentStatus } from "api/api-data-types";
import { DueDateButton } from "components/materials/page/DueDateButton";
import { PageReleaseButton } from "components/materials/page/PageReleaseButton";
import { PageMaxPoints } from "components/materials/page/content/PageMaxPoints";
import { ManageContentInteractionMode } from "components/materials/page/interaction-mode/ManageContentInteractionMode";
import { InteractionMode, SectionKind } from "components/server-types";
import { t } from "i18n/i18n";
import { ToggleSwitch } from "mds/components/ToggleSwitch";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useAppSelector } from "store/index";
import { pageSelectors, selectCurrentSectionId, selectNestedPageOutcomes } from "store/selectors";
import { selectAssessmentInfoForPage } from "store/selectors/computed";
import { useHasItemsSelector, useObjectSelector } from "store/store-hooks";
import { changePageInteractionMode, usePageActions } from "utils/page";
import { isSharedClassPage, isStaticPage } from "utils/page-utils";
import { isLinkedSection, isTemplateSection } from "utils/publishing";
import { Link } from "utils/routing/components/Link";
import { pageUrl } from "utils/urls";

const getPointsConfigTooltip = ({
  canConfigurePoints,
  isShared,
  hasAssessments,
  hasOutcomes,
  isInLinkedSection,
}: {
  canConfigurePoints: boolean;
  isShared: boolean;
  hasAssessments: boolean;
  hasOutcomes: boolean;
  isInLinkedSection: boolean;
}): string => {
  if (isInLinkedSection) {
    return t("manage_section_pages_modal.linked_section_points_tooltip");
  }

  if (canConfigurePoints) {
    return t("tooltip.assessment_points");
  }

  if (isShared) {
    return t("tooltip.points_config_shared_class");
  }

  if (hasAssessments) {
    return t("tooltip.points_config_has_assessments");
  }

  if (!hasOutcomes) {
    return t("page_overview_header.disabled_info");
  }

  return t("manage_section_pages_modal.points_disabled_tooltip");
};

export const PageRow: FC<{
  pageId: string;
  sectionKind?: SectionKind;
}> = ({ pageId, sectionKind }) => {
  const { savePage } = usePageActions();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["interaction_mode", "title", "id", "section_id", "released_at", "locked_at"],
  );
  const hasOutcomes = useHasItemsSelector((s) => selectNestedPageOutcomes(s, pageId));
  const assessmentStatusInfo = useObjectSelector(
    (s) => selectAssessmentInfoForPage(s, pageId),
    ["status", "assessed_submissions_count"],
  );
  const hasAssessments = (assessmentStatusInfo?.assessed_submissions_count ?? 0) > 0;
  const hasSubmissions = [AssessmentStatus.ASSESSABLE, AssessmentStatus.ASSESSED].includes(
    assessmentStatusInfo?.status,
  );

  if (!page) {
    return null;
  }

  const isShared = isSharedClassPage(page);
  const isStatic = isStaticPage(page);
  const isInLinkedSection = isLinkedSection(sectionKind);
  const isInTemplateSection = isTemplateSection(sectionKind);
  const canConfigurePoints = !isInLinkedSection && hasOutcomes && !isShared && !hasAssessments;

  const onChangeInteractionMode = (newInteractionMode: InteractionMode) => {
    changePageInteractionMode({
      page,
      newInteractionMode,
    });
  };

  const isReleased = !!page.released_at;
  const isLocked = !!page.locked_at;
  const href = sectionId ? pageUrl(sectionId, page.id) : "";

  return (
    <div
      className={`${getPageGridClass(isInTemplateSection)} -ml-2 border-b border-black-tint-90 py-1.5 text-xs`}
      role="row"
    >
      <div className="min-w-0 max-w-[300px]" role="cell">
        <SquareButton
          as={Link}
          className="min-w-0 max-w-full justify-start"
          kind="ghost"
          size="xs"
          title={page.title}
          to={href}
        >
          <span className="min-w-0 truncate">{page.title}</span>
        </SquareButton>
      </div>

      <div className="min-w-0" role="cell">
        <ManageContentInteractionMode
          disabled={hasSubmissions || isInLinkedSection}
          disabledTooltip={
            isInLinkedSection
              ? t("manage_section_pages_modal.linked_section_page_type_tooltip")
              : hasSubmissions
                ? t("tooltip.group_config_has_submissions")
                : undefined
          }
          pageId={pageId}
          onModeChange={onChangeInteractionMode}
        />
      </div>

      <div className="min-w-0" role="cell">
        <PageMaxPoints
          disabled={!canConfigurePoints}
          pageId={pageId}
          tooltipText={getPointsConfigTooltip({
            canConfigurePoints,
            isShared,
            hasAssessments,
            hasOutcomes,
            isInLinkedSection,
          })}
          hideIcon
          onSave={(points) => savePage({ id: pageId, max_points: points })}
        />
      </div>

      {!isInTemplateSection && (
        <>
          <div className="min-w-0" role="cell">
            <DueDateButton pageId={pageId} hideIcon shortText />
          </div>

          <div role="cell">
            <Tooltip
              content={
                isStatic
                  ? t("manage_section_pages_modal.static_lock_disabled_tooltip")
                  : isLocked
                    ? t("manage_section_pages_modal.unlock_page")
                    : t("manage_section_pages_modal.lock_page")
              }
            >
              <ToggleSwitch
                className="disabled:cursor-not-allowed"
                disabled={isStatic}
                eventName="Manage content modal - Toggle lock"
                eventProps={{ pageId, pageTitle: page.title }}
                isSelected={isLocked}
                onClick={() =>
                  savePage({
                    id: pageId,
                    locked_at: isLocked ? null : new Date().toISOString(),
                  })
                }
              />
            </Tooltip>
          </div>

          <div className="flex min-w-0 items-center gap-2" role="cell">
            <Tooltip
              content={
                isReleased
                  ? t("manage_section_pages_modal.unrelease_page")
                  : t("manage_section_pages_modal.release_page")
              }
            >
              <ToggleSwitch
                eventName="Manage content modal - Toggle release"
                eventProps={{ pageId, pageTitle: page.title }}
                isSelected={isReleased}
                onClick={() =>
                  savePage({
                    id: pageId,
                    released_at: isReleased ? null : new Date().toJSON(),
                    scheduled_release_at: null,
                  })
                }
              />
            </Tooltip>

            <PageReleaseButton pageId={pageId} />
          </div>
        </>
      )}
    </div>
  );
};
